import { Link } from 'react-router-dom'
import { User, Zap, Clock, ArrowRight } from 'lucide-react'
import type { WireArticle } from '../../api/types'
import { isActionNeeded } from './ActionNeededList'

const STAGE_COLOR: Record<string, string> = {
  qc_review:          'bg-amber-3 text-amber-11 border-amber-6',
  layout_review:      'bg-blue-3 text-blue-11 border-blue-6',
  editorial_approval: 'bg-violet-3 text-violet-11 border-violet-6',
  scheduled:          'bg-teal-3 text-teal-11 border-teal-6',
  post_pub_review:    'bg-orange-3 text-orange-11 border-orange-6',
  blocked:            'bg-red-3 text-red-11 border-red-6',
}

const STAGE_ORDER: Record<string, number> = {
  qc_review:          0,
  layout_review:      1,
  editorial_approval: 2,
  scheduled:          3,
  post_pub_review:    4,
  blocked:            5,
}

function stageLabel(s: string) {
  return s.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function byStage(a: WireArticle, b: WireArticle) {
  return (STAGE_ORDER[a.workflow?.stage ?? ''] ?? 99) - (STAGE_ORDER[b.workflow?.stage ?? ''] ?? 99)
}

interface MyAssignmentsListProps {
  articles: WireArticle[]
  userSub: string
  loading: boolean
  embedded?: boolean
}

function ArticleRow({ article, dimmed }: { article: WireArticle; dimmed: boolean }) {
  const stage = article.workflow?.stage ?? ''
  const colorClass = STAGE_COLOR[stage] ?? 'bg-fill text-fg-secondary border-line'
  return (
    <Link
      to={`/admin/articles/${article.id}`}
      className="group flex items-center justify-between gap-3 px-5 py-3 hover:bg-fill"
    >
      <div className="min-w-0 flex-1">
        <p className={`truncate font-sans text-sm font-semibold group-hover:text-accent-primary-fg ${dimmed ? 'text-fg-secondary' : 'text-fg'}`}>
          {article.title || <span className="italic">Untitled</span>}
        </p>
        <p className="mt-0.5 font-sans text-[10px] text-fg-secondary">
          {article.documentId ?? article.id}
        </p>
      </div>
      <div className="flex shrink-0 items-center gap-2">
        <span className={`rounded border px-2 py-0.5 font-sans text-[9px] font-bold uppercase tracking-wide ${colorClass}`}>
          {stageLabel(stage)}
        </span>
        <ArrowRight size={12} className="text-fg-secondary opacity-0 transition-opacity group-hover:opacity-100" />
      </div>
    </Link>
  )
}

export function MyAssignmentsList({ articles, userSub, loading, embedded = false }: MyAssignmentsListProps) {
  const actionItems = articles.filter(a => isActionNeeded(a, userSub)).sort(byStage)
  const comingUp    = articles.filter(a => !isActionNeeded(a, userSub)).sort(byStage)

  const body = (
    <div className="divide-y divide-line-subtle overflow-y-auto" style={{ maxHeight: 360 }}>
      {loading && (
        <div className="space-y-3 p-5">
          {[1, 2, 3].map(i => (
            <div key={i} className="h-10 animate-pulse rounded bg-fill" />
          ))}
        </div>
      )}

      {!loading && articles.length === 0 && (
        <div className="flex flex-col items-center justify-center py-12 text-center">
          <p className="font-sans text-sm font-semibold text-fg-secondary">No assignments</p>
          <p className="mt-1 font-sans text-xs text-fg-secondary">
            Articles assigned to you will appear here.
          </p>
        </div>
      )}

      {!loading && actionItems.length > 0 && (
        <div className="flex items-center gap-1.5 bg-amber-2 px-5 py-1.5">
          <Zap size={10} className="text-amber-11" />
          <span className="font-sans text-[9px] font-bold uppercase tracking-widest text-amber-11">
            Your Turn
          </span>
        </div>
      )}
      {!loading && actionItems.map(a => <ArticleRow key={a.id} article={a} dimmed={false} />)}

      {!loading && comingUp.length > 0 && (
        <div className="flex items-center gap-1.5 bg-fill px-5 py-1.5">
          <Clock size={10} className="text-fg-secondary" />
          <span className="font-sans text-[9px] font-bold uppercase tracking-widest text-fg-secondary">
            In Your Pipeline
          </span>
        </div>
      )}
      {!loading && comingUp.map(a => <ArticleRow key={a.id} article={a} dimmed={true} />)}
    </div>
  )

  if (embedded) {
    return (
      <div>
        {body}
        {!loading && articles.length > 0 && (
          <div className="border-t border-line px-5 py-2">
            <Link
              to="/admin/articles"
              className="font-sans text-[11px] font-semibold text-accent-primary-fg hover:underline"
            >
              Open Copy Desk →
            </Link>
          </div>
        )}
      </div>
    )
  }

  return (
    <section className="flex flex-col rounded border border-line bg-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-line px-5 py-3">
        <div className="flex items-center gap-2">
          <User size={14} className="text-fg-secondary" />
          <span className="font-sans text-[10px] font-bold uppercase tracking-widest text-fg-secondary">
            My Articles
          </span>
        </div>
        <span className="rounded-full bg-fill px-2 py-0.5 font-sans text-[10px] font-bold text-fg-secondary">
          {articles.length}
        </span>
      </div>

      {/* Body */}
      {body}

      {!loading && articles.length > 0 && (
        <div className="border-t border-line px-5 py-2">
          <Link
            to="/admin/articles"
            className="font-sans text-[11px] font-semibold text-accent-primary-fg hover:underline"
          >
            Open Copy Desk →
          </Link>
        </div>
      )}
    </section>
  )
}
